/**
 * Assignee Field Hook
 *
 * Shared hook for managing assignee field logic across Contact, Account, and Ticket forms.
 * Eliminates duplication of useGetAssigneeList integration.
 */

import { useGetAssigneeList } from 'src/pages/tickets/hooks/useGetAssigneeList';

interface UseAssigneeFieldProps {
  /**
   * Multi-field assign data from the selected record
   * (e.g., Contactassign, Companyassign, Ticketassign)
   */
  multiFieldAssign?: any[];

  /**
   * Single-field assign data for records that only have one assignee
   */
  singleFieldAssigee?: { assignedId: string; assignedType: string };

  /**
   * React Hook Form setValue function
   */
  setValue: any;

  /**
   * Flag to disable fetching/showing teams
   */
  isTeamPage?: boolean;
}

interface AssigneeFieldReturn {
  /**
   * Props to spread onto assignee field component
   */
  assigneeProps: {
    options: any[];
    searchValue: string;
    isFetching: boolean;
    open: boolean;
    listScrollHandler: (event: React.UIEvent<HTMLUListElement>) => void;
    searchValueHandler: (value: string) => void;
    openListHandler: () => void;
    closeListHandler: () => void;
  };
}

/**
 * Hook for managing assignee field state and handlers
 *
 * @param props - Configuration object
 * @returns Assignee field props ready to spread onto component
 *
 * @example
 * // In Contact Form
 * const { assigneeProps } = useAssigneeField({
 *   multiFieldAssign: selectedUser?.Contactassign,
 *   setValue,
 * });
 *
 * // In component
 * <AssigneeField {...assigneeProps} />
 */
export function useAssigneeField({
  multiFieldAssign,
  singleFieldAssigee,
  setValue,
  isTeamPage,
}: UseAssigneeFieldProps): AssigneeFieldReturn {
  const {
    _recordList,
    searchValue,
    isFetching,
    open,
    listScrollHandler,
    searchValueHandler,
    openListHandler,
    closeListHandler,
  } = useGetAssigneeList({
    multiFieldAssign,
    singleFieldAssigee,
    setValue,
    isTeamPage,
  });

  return {
    assigneeProps: {
      options: _recordList,
      searchValue,
      isFetching,
      open,
      listScrollHandler,
      searchValueHandler,
      openListHandler,
      closeListHandler,
    },
  };
}
